import bind from 'bind-decorator';
import { createPopper } from '@popperjs/core/lib/popper-lite';
import popperPreventOverflow from '@popperjs/core/lib/modifiers/preventOverflow';
import popperOffset from '@popperjs/core/lib/modifiers/offset';

import View from './view';
import globalHandler from '@/helpers/global_handler';
import showModal from '@/utils/show_modal';
import offset from '@/helpers/offset';

export default class CustomSelect extends View {
  isFocused = false;
  prevValue = null;
  popper = null;

  initialize() {
    if (!this.isV4) {
      this.$node.addClass('custom-select');
    }

    this.$select = this.$('select');
    this.$select.hide();

    this.renderPlaceholder();
    this.renderDropdown();

    if (!this.isDisabled) {
      this.modal = showModal({
        $modal: this.$dropdown,
        $trigger: this.$placeholder,
        $outerNode: this.$node,
        show: this.onShow,
        hide: this.onHide,
        isHiddenCheck: () => !this.isFocused,
        clickEventName: 'mousedown'
      });
    }

    $(document).one('turbolinks:before-cache', this.destroy);
  }

  get isV4() {
    return this.node.classList.contains('b-input-v4');
  }

  get isHtml() {
    return this.isV4 || this.node.hasAttribute('data-is_html_options');
  }

  get isDynamicDropdownHeight() {
    return this.node.hasAttribute('data-is_dynamic_dropdown_height');
  }

  get currentValue() {
    return this.$select.val();
  }

  get $currentOption() {
    return this.$select.find(`option[value='${this.currentValue}']`).first();
  }

  get $label() {
    return this.$('label');
  }

  get isDisabled() {
    return this.$node.hasClass('disabled') ||
      this.$select.is(':disabled');
  }

  @bind
  onShow() {
    this.isFocused = true;

    requestAnimationFrame(() => {
      this.$node.addClass('is-focused');
      this.$placeholder.addClass('is-focused');
    });
    this.$dropdown.removeClass('hidden');

    const dropdownOffset = 4;
    const dropdownPadding = 10;

    if (this.isDynamicDropdownHeight) {
      const maxHeight = this.calculateDropdownMaxHeight(dropdownOffset, dropdownPadding);
      if (maxHeight) {
        this.$dropdown.css('max-height', `${maxHeight}px`);
      }
    }

    this.popper = createPopper(
      this.$placeholder[0],
      this.$dropdown[0],
      {
        placement: 'bottom-start',
        modifiers: [popperPreventOverflow, popperOffset, {
          name: 'preventOverflow',
          options: { padding: dropdownPadding }
        }, {
          name: 'offset',
          options: { offset: [0, dropdownOffset] }
        }, {
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
          }
        }]
      }
    );

    globalHandler
      .on('up', this.onPressUp)
      .on('down', this.onPressDown)
      .on('enter', this.onPressEnter);

    this.prevValue = this.currentValue;
  }

  @bind
  onHide() {
    this.isFocused = false;

    requestAnimationFrame(() => {
      this.$node.removeClass('is-focused');
      this.$placeholder.removeClass('is-focused');
    });
    this.$dropdown.addClass('hidden');

    if (this.popper) {
      this.popper.destroy();
      this.popper = null;
    }

    globalHandler
      .off('up', this.onPressUp)
      .off('down', this.onPressDown)
      .off('enter', this.onPressEnter);

    if (this.prevValue !== this.currentValue) {
      this.$node.trigger('custom-select:change');
    }
  }

  @bind
  onOptionClick({ currentTarget, isSkipModalHide }) {
    this.selectOption(currentTarget.getAttribute('data-value'));

    if (!isSkipModalHide) {
      this.$dropdown.trigger('modal:hide');
    }
  }

  @bind
  onPressEnter() {
    this.$dropdown.trigger('modal:hide');
  }

  @bind
  onPressUp(e) {
    const $newOption = this.$('.option.is-current').first().prevAll('.option').first();
    if (!$newOption.length) { return; }

    e.preventDefault();
    e.stopImmediatePropagation();

    this.selectOption($newOption[0].getAttribute('data-value'));
  }

  @bind
  onPressDown(e) {
    const $newOption = this.$('.option.is-current').last().nextAll('.option').first();
    if (!$newOption.length) { return; }

    e.preventDefault();
    e.stopImmediatePropagation();

    this.selectOption($newOption[0].getAttribute('data-value'));
  }

  @bind
  destroy() {
    if (this.popper) {
      this.popper.destroy();
      this.popper = null;
    }
    this.$placeholder.remove();
    this.$dropdown.remove();
    this.$select.show();
    this.modal?.destroy();
  }

  renderPlaceholder() {
    this.$placeholder = $(`
      <div class="custom-select-placeholder">
        <div class="placeholder-inner"></div>
` + (
      this.isV4 ?
        '' :
        `<div class="b-svg-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
            <g clip-path="url(#clip0_112_999)">
              <path d="M3.68784 1.09443L0.127804 4.65453C0.045398 4.73687 0 4.84679 0 4.96399C0 5.08119 0.045398 5.19111 0.127804 5.27345L0.389915 5.53563C0.560711 5.70623 0.838302 5.70623 1.00884 5.53563L3.99834 2.54612L6.99116 5.53894C7.07357 5.62128 7.18342 5.66675 7.30056 5.66675C7.41782 5.66675 7.52768 5.62128 7.61015 5.53894L7.87219 5.27677C7.9546 5.19436 8 5.08451 8 4.96731C8 4.8501 7.9546 4.74019 7.87219 4.65785L4.30891 1.09443C4.22624 1.01189 4.11587 0.966558 3.99854 0.966818C3.88075 0.966558 3.77044 1.01189 3.68784 1.09443Z" fill="currentColor"/>
            </g>
            <defs>
              <clipPath id="clip0_112_999">
                <rect width="8" height="8" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </div>`) + `
      </div>
    `);

    if (this.isDisabled) {
      this.$placeholder.addClass('is-disabled');
      if (!this.isV4) {
        this.$placeholder.find('.b-svg-icon').addClass('is-disabled');
      }
    } else {
      this.$placeholder.prop('tabindex', '0');
    }

    this.syncCurrentOption(true, false);

    this.$placeholder.insertAfter(this.$label.length ? this.$label : this.$select);
  }

  renderDropdown() {
    this.$dropdown = $(`
      <div class="custom-select-dropdown hidden" data-scroll-lock-scrollable></div>
    `);

    this.$select.find('option').each((_index, node) => {
      const groupStartClass = node.hasAttribute('data-divider') ? ' is-group-start' : '';
      this.$dropdown.append(`
        <div class="option${groupStartClass}" data-value="${node.value}">
          ${this.isHtml ? node.innerHTML : node.innerText}
        </div>
      `);
    });

    const footer = this.$select[0].getAttribute('data-dropdown_footer');
    if (footer) {
      this.$dropdown.append(`
        <div class="custom-select-dropdown-footer">${footer}</div>
      `);
    }

    this.syncCurrentOption(false, true);
    this.on('click', '.custom-select-dropdown .option', this.onOptionClick);

    this.$dropdown.insertAfter(this.$placeholder);
  }

  selectOption(value) {
    this.$select
      .val(value)
      .trigger('change');
    this.syncCurrentOption();
  }

  syncCurrentOption(isSyncPlceholder = true, isSyncOption = true) {
    if (isSyncPlceholder) {
      const $placeholderInner = $('<div class="placeholder-inner" />')
        .html(this.isHtml ? this.$currentOption.html() : this.$currentOption.text());
      const $placeholderInnerText = $placeholderInner.find('.placeholder-inner-text');

      if ($placeholderInnerText.length) {
        $placeholderInner.html($placeholderInnerText.html());
      }

      this.$placeholder
        .find('.placeholder-inner')
        .replaceWith($placeholderInner);
    }

    if (isSyncOption) {
      this.$dropdown
        .find('.option')
        .removeClass('is-current');

      this.$dropdown
        .find(`.option[data-value='${this.currentValue}']`)
        .addClass('is-current');
    }
  }

  calculateDropdownMaxHeight(dropdownOffset, dropdownPadding) {
    const placeholder = this.$placeholder[0];

    if (!this.isInsideFixedContainer(placeholder)) {
      return null;
    }

    const dropdown = this.$dropdown[0];
    const placeholderBottom = offset(placeholder).top + placeholder.getBoundingClientRect().height;

    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const spaceBelow = viewportHeight - placeholderBottom;

    return Math.min(
      Math.max(150, spaceBelow - dropdownOffset - dropdownPadding),
      dropdown.clientHeight
    );
  }

  isInsideFixedContainer(element) {
    let current = element;

    while (current && current !== document.body) {
      const position = window.getComputedStyle(current).position;

      if (position === 'fixed') {
        return true;
      }
      current = current.parentElement;
    }

    return false;
  }
}
